import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, downloadProtectedFile } from "@/lib/auth";
import {
  buildCustomerEmailDraft,
  buildSupplierEmailDraft,
  buildQuoteEmailDraft,
  gmailComposeUrl,
  whatsappUrl,
} from "@/lib/outreach";
import { Mail, MessageCircle, ExternalLink, Paperclip } from "lucide-react";
import Modal from "@/components/Modal";
import { toast } from "sonner";

/**
 * SendMessageModal — compose an Email (Gmail) or WhatsApp message to a customer
 * or supplier (pass kind="supplier"). Both channels open a prefilled compose
 * window in the app already installed/logged in on the user's device
 * (Gmail / WhatsApp) — no provider credentials. Quotes only apply to customers.
 */
export default function SendMessageModal({ channel, customer, quoteId, kind = "customer", onClose, onSent }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [fromUserId, setFromUserId] = useState(user?.id || "");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [quote, setQuote] = useState(null);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const isEmail = channel === "email";
  const isSupplier = kind === "supplier";
  const apiBase = isSupplier ? "/suppliers" : "/customers";
  const fromUser = isEmail ? user : (users.find((x) => x.id === fromUserId) || user);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (isEmail) {
          if (quoteId) {
            const r = await api.get(`/quotations/${quoteId}`);
            if (!alive) return;
            setQuote(r.data);
          }
        } else {
          const [p, u] = await Promise.all([
            api.post(`${apiBase}/${customer.id}/preview-message`, null, { params: { channel, quote_id: quoteId || undefined } }),
            api.get("/users"),
          ]);
          if (!alive) return;
          setUsers(u.data);
          setTo(p.data.to || "");
          setBody(p.data.body || "");
        }
      } catch (e) {
        toast.error("Failed to prepare message");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [channel, customer.id, quoteId, isEmail, apiBase]);

  // Build or refresh the Gmail draft from the logged-in user.
  useEffect(() => {
    if (!isEmail || loading || !user) return;
    const draft = quote
      ? buildQuoteEmailDraft(quote, customer, user)
      : isSupplier
        ? buildSupplierEmailDraft(customer, user)
        : buildCustomerEmailDraft(customer, user);
    setTo(draft.to || "");
    setSubject(draft.subject || "");
    setBody(draft.body || "");
  }, [isEmail, loading, quote, customer, user, isSupplier]);

  const send = async () => {
    if (!to.trim()) return toast.error(`${isEmail ? "Email" : "Phone"} is required`);
    if (isEmail) {
      if (quote) {
        try {
          await downloadProtectedFile(`/quotations/${quote.id}/pdf`, `Quotation_${quote.quote_no.replace(/\//g, "-")}.pdf`);
          toast.success("Quotation PDF downloaded — attach it to the Gmail draft", { duration: 6000 });
        } catch (e) {
          toast.error("Couldn't download the quotation PDF", { description: e.response?.data?.detail || e.message });
        }
      }
      window.open(gmailComposeUrl({ to: to.trim(), subject, body }), "_blank", "noopener,noreferrer");
      toast.success("Opening Gmail…");
      onClose();
      return;
    }
    // WhatsApp: open the recipient's chat in the app installed on this device
    // (wa.me click-to-chat) rather than sending via a server-side provider.
    setSending(true);
    try {
      const sig = `\n\n— ${fromUser?.name || ""}\n${fromUser?.phone || ""}`;
      const fullBody = sig.trim() && !body.includes(sig.trim()) ? body + sig : body;
      window.open(whatsappUrl({ to: to.trim(), body: fullBody }), "_blank", "noopener,noreferrer");
      toast.success("Opening WhatsApp…");
      await api.post(`${apiBase}/${customer.id}/send-whatsapp`, { to: to.trim(), body: fullBody, quote_id: quoteId, from_user_id: fromUserId });
      onSent && onSent();
      onClose();
    } catch (e) {
      // The chat still opened even if activity logging failed — don't block the user on it.
      onSent && onSent();
      onClose();
    } finally {
      setSending(false);
    }
  };

  const title = isEmail ? "Send Email" : "Send WhatsApp";
  const sub = `To ${customer.company}` + (quoteId ? " · with quotation" : "");
  const Icon = isEmail ? Mail : MessageCircle;
  const emailOptions = customer?.emails || [];
  // The "To" field holds a comma-separated recipient list so a customer with
  // several emails can be sent to all at once (Gmail accepts multiple).
  const selectedEmails = to.split(",").map((s) => s.trim()).filter(Boolean);
  const toggleEmail = (v) => {
    const next = selectedEmails.includes(v)
      ? selectedEmails.filter((e) => e !== v)
      : [...selectedEmails, v];
    setTo(next.join(", "));
  };

  return (
    <Modal title={title} subtitle={sub} onClose={onClose} size="lg">
      <div data-testid={`send-${channel}-modal`}>
        {loading ? (
          <div style={{ padding: 32, textAlign: "center", color: "var(--text-2)" }}>Preparing message…</div>
        ) : (
          <>
            <div className="grid gap-4">
              {isEmail && (
                <div style={{ fontSize: 12, color: "var(--text-2)" }}>
                  Edit the draft below, then open it in Gmail to send from your account.
                </div>
              )}

              {isEmail && quote && (
                <div className="card-ledger flex items-start gap-3" style={{ padding: 12, background: "#FFF7EC", borderColor: "#F4D2BB" }} data-testid="quote-pdf-attach-banner">
                  <Paperclip size={16} style={{ color: "var(--primary)", flexShrink: 0, marginTop: 2 }} />
                  <div style={{ fontSize: 12, color: "var(--text-2)" }}>
                    <strong style={{ color: "var(--text)" }}>Quotation PDF will download</strong> when you click below —
                    Gmail's compose window can't attach files automatically, so drag the downloaded file into the draft before sending.
                  </div>
                </div>
              )}

              {isEmail && !quote && (
                <div className="card-ledger flex items-start gap-3" style={{ padding: 12, background: "#FFF7EC", borderColor: "#F4D2BB" }} data-testid="product-images-attach-banner">
                  <Paperclip size={16} style={{ color: "var(--primary)", flexShrink: 0, marginTop: 2 }} />
                  <div style={{ fontSize: 12, color: "var(--text-2)" }}>
                    <strong style={{ color: "var(--text)" }}>Attach product images</strong> — this draft mentions attached photos, but
                    Gmail's compose window can't add files automatically, so drag them into the draft before sending.
                  </div>
                </div>
              )}

              {/* Sender — email is always from the logged-in user; WhatsApp lets you pick */}
              <div>
                <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>From (sender)</label>
                {isEmail ? (
                  user && (
                    <div className="input-base" style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--surface-2)" }} data-testid={`${channel}-from`}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: user.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 13 }}>
                        <strong>{user.name}</strong>
                        <span className="font-mono-data" style={{ color: "var(--text-2)", marginLeft: 8 }}>{user.email}</span>
                      </span>
                    </div>
                  )
                ) : (
                  <>
                    <select value={fromUserId} onChange={(e) => setFromUserId(e.target.value)} className="input-base" data-testid={`${channel}-from`}>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} — {u.phone}
                        </option>
                      ))}
                    </select>
                    {fromUser && (
                      <div style={{ fontSize: 11, color: "var(--text-2)", marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: fromUser.color }} />
                        Sending as <strong style={{ color: "var(--text)" }}>{fromUser.name}</strong> · <span className="font-mono-data">{fromUser.phone}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div>
                <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>
                  {isEmail ? "To (email)" : "To (phone, E.164)"}
                </label>
                <input value={to} onChange={(e) => setTo(e.target.value)} className="input-base" placeholder={isEmail ? "name@company.com, other@company.com" : "+971 50 123 4567"} data-testid={`${channel}-to`} />
                {isEmail && emailOptions.length > 1 && (
                  <div className="flex flex-wrap gap-1 mt-2 items-center" data-testid="email-recipient-chips">
                    <span style={{ fontSize: 10, color: "var(--text-2)", marginRight: 2 }}>Tap to add / remove recipients:</span>
                    {emailOptions.map((v) => {
                      const active = selectedEmails.includes(v);
                      return (
                        <button
                          key={v}
                          type="button"
                          onClick={() => toggleEmail(v)}
                          className="tag"
                          data-testid={`email-recipient-${v}`}
                          style={{
                            fontSize: 10,
                            cursor: "pointer",
                            background: active ? "var(--primary)" : "var(--surface-2)",
                            borderColor: active ? "var(--primary)" : "var(--border)",
                            color: active ? "#fff" : "var(--text-2)",
                          }}
                        >
                          {v}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {isEmail && (
                <div>
                  <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Subject</label>
                  <input value={subject} onChange={(e) => setSubject(e.target.value)} className="input-base" data-testid="email-subject" />
                </div>
              )}

              <div>
                <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Message</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={isEmail ? 12 : 8}
                  className="input-base"
                  style={{ resize: "vertical", fontFamily: "inherit", whiteSpace: "pre-wrap" }}
                  data-testid={isEmail ? "email-body" : "whatsapp-body"}
                />
                {!isEmail && (
                  <div style={{ fontSize: 11, color: "var(--text-2)", marginTop: 4 }}>
                    {`Note: your name and phone (${fromUser?.phone || ""}) are appended automatically.`}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button onClick={onClose} className="btn-outline" data-testid={`${channel}-cancel`}>Cancel</button>
              <button onClick={send} disabled={sending} className="btn-primary" data-testid={`${channel}-send`} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Icon size={14} />
                {sending ? "Opening…" : isEmail ? "Open in Gmail" : "Open in WhatsApp"}
                <ExternalLink size={13} />
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
